import React from 'react';

import { RecurrenceScope } from '@/typings/models/Recurrence';
import RecurrenceScopeDrawer, { RecurrenceScopeAction } from './RecurrenceScopeDrawer';

interface AskRecurrenceScopeOptions {
  action: RecurrenceScopeAction;
  /** Hide the "This event" option (e.g. the recurrence rule itself changed). */
  allowThisEvent?: boolean;
  defaultScope?: RecurrenceScope;
}

interface ScopeDrawerState extends AskRecurrenceScopeOptions {
  open: boolean;
}

/**
 * Imperative access to the {@link RecurrenceScopeDrawer}. `askRecurrenceScope`
 * resolves with the chosen {@link RecurrenceScope}, or `null` if cancelled —
 * which works equally well from event handlers (delete, drag/resize) and from
 * promise chains (the drawer's Save). Render `recurrenceScopeDrawer` once.
 *
 * @example
 * const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
 * const scope = await askRecurrenceScope({ action: 'delete' });
 * if (!scope) return; // cancelled
 */
export function useRecurrenceScope() {
  const resolverRef = React.useRef<((scope: RecurrenceScope | null) => void) | null>(null);
  const [state, setState] = React.useState<ScopeDrawerState>({ open: false, action: 'edit' });

  const askRecurrenceScope = React.useCallback(
    (options: AskRecurrenceScopeOptions) =>
      new Promise<RecurrenceScope | null>((resolve) => {
        resolverRef.current = resolve;
        setState({ open: true, ...options });
      }),
    [],
  );

  const settle = React.useCallback((scope: RecurrenceScope | null) => {
    resolverRef.current?.(scope);
    resolverRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const recurrenceScopeDrawer = (
    <RecurrenceScopeDrawer
      open={state.open}
      action={state.action}
      allowThisEvent={state.allowThisEvent}
      defaultScope={state.defaultScope}
      onConfirm={settle}
      onCancel={() => settle(null)}
    />
  );

  return { askRecurrenceScope, recurrenceScopeDrawer };
}
